import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { getAuthToken } from 'Api';

// TD1-10212: collect a us_bank_account PaymentMethod via the Stripe SDK (Financial Connections),
// replacing the legacy Plaid link flow for storefront ACH. Calls the SetupIntent endpoint
// (StripeService::BankAccountSetupIntent), runs collectBankAccountForSetup + confirm, and returns the
// resulting PaymentMethod id (pm_...) to submit as new_bank: { payment_method_id }.
//
// VERIFY IN STRIPE TEST MODE: Financial-Connections-on-Connect (Stripe.js initialized with the
// connected { stripeAccount }), the returned client_secret, and the collect/confirm result shape.
export async function collectStripeBankAccount({
  rentalToken,
  locationId,
  customerId,
  customerType,
  billingDetails,
}) {
  // Rental-nested for checkout on an existing rental; location-scoped for the autobook/cart
  // flow where no rental exists yet (both hit Portal::TransactionsController#setup_bank_account).
  const setupUrl = rentalToken
    ? `${process.env.REACT_APP_API_DOMAIN}/api/portal/rentals/${rentalToken}/transactions/setup_bank_account`
    : `${process.env.REACT_APP_API_DOMAIN}/api/portal/locations/${locationId}/setup_bank_account`;

  const { data } = await axios.post(
    setupUrl,
    { location_id: locationId, customer_id: customerId, customer_type: customerType },
    { headers: getAuthToken() }
  );

  const clientSecret = data.client_secret;
  const stripeAccount = data.stripe_account;

  const stripe = await loadStripe(
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY,
    stripeAccount ? { stripeAccount } : undefined
  );
  if (!stripe) throw new Error('Unable to load Stripe.');

  const collectResult = await stripe.collectBankAccountForSetup({
    clientSecret,
    params: {
      payment_method_type: 'us_bank_account',
      payment_method_data: { billing_details: billingDetails },
    },
    expand: ['payment_method'],
  });
  if (collectResult.error) throw new Error(collectResult.error.message);

  let setupIntent = collectResult.setupIntent;
  if (setupIntent && setupIntent.status === 'requires_confirmation') {
    const confirmResult = await stripe.confirmUsBankAccountSetup(clientSecret);
    if (confirmResult.error) throw new Error(confirmResult.error.message);
    setupIntent = confirmResult.setupIntent;
  }

  const paymentMethod = setupIntent && setupIntent.payment_method;
  const paymentMethodId =
    typeof paymentMethod === 'string' ? paymentMethod : paymentMethod && paymentMethod.id;
  if (!paymentMethodId) throw new Error('Bank account was not collected.');
  return paymentMethodId;
}
